// ============================================================================
// NEİZLESEM - Catalog API client
// Talks to our FastAPI backend (/api/catalog/*) which proxies the keyless
// Cinemeta data source (+ optional TMDB enrichment). Central service layer.
// ============================================================================

import type {
  ContentItem,
  ContentDetail,
  ContentType,
  RecommendResult,
} from './types'

const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api'

export class ApiError extends Error {
  status: number
  constructor(message: string, status = 500) {
    super(message)
    this.status = status
  }
}

async function getJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_URL}${path}`
  let res: Response
  try {
    res = await fetch(url, { ...init, headers: { Accept: 'application/json' } })
  } catch (e) {
    // Network/offline error - surface a friendly message, log the technical one.
    console.error('[api] network error', url, e)
    throw new ApiError('Bağlantı hatası', 0)
  }
  if (!res.ok) {
    let detail = 'İstek başarısız oldu'
    try {
      const body = await res.json()
      detail = body.detail || detail
    } catch {
      /* ignore */
    }
    console.error('[api] error', url, res.status, detail)
    throw new ApiError(detail, res.status)
  }
  return res.json()
}

// ----------------------------------------------------------------------------
// Meta
// ----------------------------------------------------------------------------
export interface CatalogMeta {
  genres: string[]
  tmdbEnrichment: boolean
  region: string
}

export function getMeta(): Promise<CatalogMeta> {
  return getJSON<CatalogMeta>('/catalog/meta')
}

// ----------------------------------------------------------------------------
// Catalog / listing
// ----------------------------------------------------------------------------
export type SortKey =
  | 'popular'
  | 'rating'
  | 'rating_asc'
  | 'newest'
  | 'oldest'

export interface ListParams {
  type: ContentType
  genre?: string | null
  sort?: SortKey
  minRating?: number | null
  yearFrom?: number | null
  skip?: number
}

export async function listContent(params: ListParams): Promise<ContentItem[]> {
  const q = new URLSearchParams()
  q.set('type', params.type)
  if (params.genre) q.set('genre', params.genre)
  if (params.sort) q.set('sort', params.sort)
  if (params.minRating) q.set('min_rating', String(params.minRating))
  if (params.yearFrom) q.set('year_from', String(params.yearFrom))
  if (params.skip) q.set('skip', String(params.skip))
  const data = await getJSON<{ results: ContentItem[] }>(`/catalog/list?${q.toString()}`)
  return data.results
}

// ----------------------------------------------------------------------------
// Search
// ----------------------------------------------------------------------------
export interface SearchResult {
  movies: ContentItem[]
  series: ContentItem[]
  total: number
}

export async function searchAll(query: string): Promise<SearchResult> {
  const q = new URLSearchParams({ q: query, type: 'all' })
  const data = await getJSON<{ movies: ContentItem[]; series: ContentItem[]; total: number }>(
    `/catalog/search?${q.toString()}`
  )
  return { movies: data.movies || [], series: data.series || [], total: data.total || 0 }
}

// ----------------------------------------------------------------------------
// Detail + similar
// ----------------------------------------------------------------------------
export function getDetail(type: ContentType, id: string): Promise<ContentDetail> {
  return getJSON<ContentDetail>(`/catalog/detail/${type}/${encodeURIComponent(id)}`)
}

export async function getSimilar(
  type: ContentType,
  id: string,
  genre?: string | null
): Promise<ContentItem[]> {
  const q = new URLSearchParams()
  if (genre) q.set('genre', genre)
  const data = await getJSON<{ results: ContentItem[] }>(
    `/catalog/similar/${type}/${encodeURIComponent(id)}?${q.toString()}`
  )
  return data.results
}

// ----------------------------------------------------------------------------
// Recommendation ("Bana Bir Şey Bul")
// ----------------------------------------------------------------------------
export interface RecommendParams {
  type?: string
  genre?: string | null
  mood?: string | null
  duration?: string | null
  era?: string | null
  exclude?: string[]
}

export async function recommend(params: RecommendParams): Promise<RecommendResult> {
  const q = new URLSearchParams()
  if (params.type) q.set('type', params.type)
  if (params.genre) q.set('genre', params.genre)
  if (params.mood) q.set('mood', params.mood)
  if (params.duration) q.set('duration', params.duration)
  if (params.era) q.set('era', params.era)
  if (params.exclude && params.exclude.length) q.set('exclude', params.exclude.join(','))
  return getJSON<RecommendResult>(`/catalog/recommend?${q.toString()}`)
}

export type { ContentItem, ContentDetail, ContentType, RecommendResult }
